import { format } from "date-fns";
import { useMemo, useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Switch } from "@/components/ui/switch";

import { MEDDREAM_LABELS } from "../labels";
import type { IMeddreamHourlyMetric } from "../schemas/meddream-hourly";

export interface HourlyMetricsTableProps {
  metrics: IMeddreamHourlyMetric[];
  isLoading?: boolean;
}

function HeaderWithHint({
  label,
  description,
}: {
  label: string;
  description: string;
}): React.JSX.Element {
  return (
    <span title={description} className="cursor-help">
      {label}
    </span>
  );
}

/**
 * Tabla horaria densа con filtro opcional de reinicios.
 */
export function HourlyMetricsTable({
  metrics,
  isLoading,
}: HourlyMetricsTableProps): React.JSX.Element {
  const [onlyRestarts, setOnlyRestarts] = useState(false);

  const data = useMemo(() => {
    const sorted = [...metrics].sort((a, b) => a.hourStartMs - b.hourStartMs);
    return onlyRestarts ? sorted.filter((m) => m.restartsCount > 0) : sorted;
  }, [metrics, onlyRestarts]);

  const columns = useMemo<ColumnDef<IMeddreamHourlyMetric>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Hora UTC",
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.id}</span>
        ),
      },
      {
        id: "local",
        header: "Local",
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {format(row.original.hourStart, "dd/MM HH:mm")}
          </span>
        ),
      },
      {
        accessorKey: "activeConnectionsMax",
        header: () => (
          <HeaderWithHint
            label="Pico"
            description={MEDDREAM_LABELS.peakConnections.description}
          />
        ),
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.activeConnectionsMax}</span>
        ),
      },
      {
        accessorKey: "activeConnectionsMin",
        header: () => (
          <HeaderWithHint
            label="Mín"
            description={MEDDREAM_LABELS.minConnections.description}
          />
        ),
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.activeConnectionsMin}</span>
        ),
      },
      {
        accessorKey: "activeConnectionsAvg",
        header: () => (
          <HeaderWithHint
            label={MEDDREAM_LABELS.avgConnections.label}
            description={MEDDREAM_LABELS.avgConnections.description}
          />
        ),
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.activeConnectionsAvg}</span>
        ),
      },
      {
        accessorKey: "uniqueSessionsCount",
        header: () => (
          <HeaderWithHint
            label="Sesiones"
            description={MEDDREAM_LABELS.sessionsInHour.description}
          />
        ),
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.uniqueSessionsCount}</span>
        ),
      },
      {
        accessorKey: "restartsCount",
        header: () => (
          <HeaderWithHint
            label="Reinicios"
            description={MEDDREAM_LABELS.restarts.description}
          />
        ),
        cell: ({ row }) => {
          const n = row.original.restartsCount;
          return n > 0 ? (
            <Badge variant="destructive" className="tabular-nums">
              {n}
            </Badge>
          ) : (
            <span className="tabular-nums text-muted-foreground">0</span>
          );
        },
      },
      {
        accessorKey: "samplesCount",
        header: () => (
          <HeaderWithHint
            label="Mediciones"
            description={MEDDREAM_LABELS.samples.description}
          />
        ),
        cell: ({ row }) => (
          <span
            className="tabular-nums"
            title={MEDDREAM_LABELS.samples.description}
          >
            {row.original.samplesCount}
          </span>
        ),
      },
      {
        id: "updatedAt",
        header: () => (
          <HeaderWithHint
            label={MEDDREAM_LABELS.updatedAt.label}
            description={MEDDREAM_LABELS.updatedAt.description}
          />
        ),
        cell: ({ row }) =>
          row.original.updatedAt ? (
            <span className="font-mono text-xs">
              {format(row.original.updatedAt, "HH:mm:ss")}
            </span>
          ) : (
            "—"
          ),
      },
    ],
    []
  );

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle>Detalle horario</CardTitle>
          <CardDescription>
            Métricas agregadas por hora UTC · pasa el cursor sobre los encabezados
            para ver qué significa cada columna
          </CardDescription>
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={onlyRestarts} onCheckedChange={setOnlyRestarts} />
          Solo reinicios
        </label>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={data}
          loading={isLoading}
          showSearch={false}
          showFilters={false}
          pageSize={12}
          emptyMessage={
            onlyRestarts
              ? "No hay horas con reinicios en este rango"
              : "No hay métricas para este rango"
          }
          className="w-full"
        />
      </CardContent>
    </Card>
  );
}
